import type { OmniSearchResult } from '@consumer/types/serializers'

export const SESSION_KEY = 'giftly.internal_tracking_data' as const
export const SEARCH_ORIGIN_SESSION_KEY = 'giftly.omni_search_origin' as const

interface LandingPageTrackingData {
  modelType: 'landing_page'
  modelData: {
    slug: string
    variant: string
  }
}
interface EventTrackingData {
  modelType: 'event'
  modelData: {
    event_id: number
    event_name: string
    event_subtype: string
  }
}
type TrackingData = LandingPageTrackingData | EventTrackingData

// Keys stay camelCase here; `deepDecamelizeKeys` in shared/services/api.ts snake_cases the whole
// body on its way to Rails, so this lands as `clicked_on` / `searchable_id` in the jsonb column.
export interface OmniSearchOrigin {
  query: string
  clickedOn: string
  searchableId?: number
  searchableType?: OmniSearchResult['searchableType']
}

function readSession<T> (key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T | null
  }
  catch {
    return null
  }
}

function writeSession (key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  }
  catch (e) {
    console.error(`Failed to set ${key}`, e)
  }
}

// The omni search origin lives under its own key because a destination page can set its own
// tracking data on top of it — a search result that lands on a landing page would otherwise lose
// the search attribution before the gift reaches the cart. That means search-originated purchases
// surface under two different shapes; see doc/PURCHASE-ATTRIBUTION.md before querying the column.
export function getInternalOrderGiftTracking () {
  const tracking = readSession<TrackingData>(SESSION_KEY)
  const origin = readSession<OmniSearchOrigin>(SEARCH_ORIGIN_SESSION_KEY)

  if (!tracking?.modelType) {
    if (!origin?.clickedOn) return null

    return {
      internalTrackingData: { modelType: 'omni_search', modelData: origin },
    }
  }

  if (!origin?.clickedOn) return { internalTrackingData: tracking }

  return {
    internalTrackingData: {
      ...tracking,
      modelData: { ...tracking.modelData, originSearch: origin },
    },
  }
}

export function clearInternalOrderGiftTracking () {
  try {
    sessionStorage.removeItem(SESSION_KEY)
    sessionStorage.removeItem(SEARCH_ORIGIN_SESSION_KEY)
  }
  catch (e) {
    console.error('Failed to clear internal order gift tracking data', e)
  }
}

export function setInternalOrderGiftTracking (data: TrackingData) {
  writeSession(SESSION_KEY, data)
}

export function setOmniSearchOrigin (origin: OmniSearchOrigin) {
  writeSession(SEARCH_ORIGIN_SESSION_KEY, origin)
}

// A search that ends somewhere with no purchase intent (login, support, card activation) drops any
// origin we were holding, so an unrelated gift bought later in the same tab isn't attributed to it.
export function clearOmniSearchOrigin () {
  try {
    sessionStorage.removeItem(SEARCH_ORIGIN_SESSION_KEY)
  }
  catch (e) {
    console.error('Failed to clear omni search origin', e)
  }
}
